import { cva } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { cn } from '../../lib/cn'

/**
 * The Avatars set (193:5261): a 36px initials disc on one of the five Figma tints.
 *
 * `size="sm"` is the 32px disc the Profiles components draw (498:8578, 498:8643):
 * Medium 12px initials in Dark rather than the set's 15.75px in its own ink. Those
 * components also paint two tints the Avatars set lacks, `blue` and `peach`.
 *
 * `size="comment"` is the 36.125px disc of the Comments author (463:50763): Medium
 * 14.314px initials in Dark on a 10.224px line, set in Mona Sans SemiExpanded.
 * `size="card"` is the same disc on the Horizontal card designer (445:44853), whose
 * initials are the same Medium 14.314px at normal width.
 */
export const avatarVariants = cva(
	'inline-flex shrink-0 items-center justify-center rounded-atlas-full font-atlas-sans whitespace-nowrap select-none',
	{
		variants: {
			size: {
				md: 'size-9 text-atlas-avatar text-atlas-avatar-ink',
				sm: 'size-8 text-atlas-avatar-sm text-atlas-dark',
				comment: 'size-atlas-avatar-comment text-atlas-avatar-comment font-stretch-semi-expanded text-atlas-dark',
				card: 'size-atlas-avatar-comment text-atlas-avatar-comment text-atlas-dark',
			},
			tint: {
				lila: 'bg-atlas-tint-lila',
				yellow: 'bg-atlas-tint-yellow',
				pink: 'bg-atlas-tint-pink',
				orange: 'bg-atlas-tint-orange',
				green: 'bg-atlas-tint-green',
				blue: 'bg-atlas-tint-blue',
				peach: 'bg-atlas-tint-peach',
			},
		},
		defaultVariants: { size: 'md', tint: 'lila' },
	},
)

/**
 * The tints the Figma draws at each disc size, and only those: the Avatars set's
 * five at `md`; yellow, lila, blue and peach on the Profiles' 32px disc; yellow and
 * lila on the 36.125px disc of the Comments and the designer card.
 */
export interface AvatarTintBySize {
	md: 'lila' | 'yellow' | 'pink' | 'orange' | 'green'
	sm: 'yellow' | 'lila' | 'blue' | 'peach'
	comment: 'yellow' | 'lila'
	card: 'yellow' | 'lila'
}

export type AvatarSize = keyof AvatarTintBySize

interface AvatarBaseProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
	/** The initials drawn in the disc, e.g. "HF". */
	initials: string
	/** The person's full name, announced in place of the initials. */
	name?: string
}

type SizedAvatarProps<S extends AvatarSize> = AvatarBaseProps & { size: S; tint?: AvatarTintBySize[S] }

/** A size and a tint the Figma draws together; `size` defaults to `md`, `tint` to lila. */
export type AvatarProps =
	| (AvatarBaseProps & { size?: 'md'; tint?: AvatarTintBySize['md'] })
	| SizedAvatarProps<'sm'>
	| SizedAvatarProps<'comment'>
	| SizedAvatarProps<'card'>

/** A person's initials on a tinted disc. */
export function Avatar({ initials, name, size, tint, className, ...props }: AvatarProps) {
	return (
		<span {...props} role="img" aria-label={name ?? initials} className={cn(avatarVariants({ size, tint }), className)}>
			<span aria-hidden="true">{initials}</span>
		</span>
	)
}
